/**
 * ThemeSwitch — three-mode segmented control mounted in the Topbar.
 *
 * Per spec §3.4 (zoneium-v2-reskin-spec.md):
 *   - Modes: dark (default) · light · mixup (high-contrast WCAG AAA)
 *   - Persists to localStorage under key 'zoneium-theme'
 *   - Switches via the `data-theme` attribute on <body>
 *   - FOUC prevented by an inline script in index.html that sets the
 *     attribute before React mounts (see frontend/index.html)
 *
 * Accessibility: tablist/tab roles + aria-selected. Visual swatch dot
 * gives a quick glance at which mode is active.
 */

import { useEffect, useState } from 'react';
import styles from './ThemeSwitch.module.css';

export type Theme = 'dark' | 'light' | 'mixup';
export const THEME_STORAGE_KEY = 'zoneium-theme';

const THEMES: Theme[] = ['dark', 'light', 'mixup'];

function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  const saved = window.localStorage.getItem(THEME_STORAGE_KEY);
  return saved && (THEMES as string[]).includes(saved) ? (saved as Theme) : 'dark';
}

export default function ThemeSwitch() {
  const [theme, setTheme] = useState<Theme>(readInitialTheme);

  useEffect(() => {
    document.body.setAttribute('data-theme', theme);
  }, [theme]);

  const apply = (t: Theme) => {
    setTheme(t);
    window.localStorage.setItem(THEME_STORAGE_KEY, t);
  };

  return (
    <div
      className={styles.switch}
      role="tablist"
      aria-label="Theme mode"
      data-testid="theme-switch"
    >
      {THEMES.map((t) => (
        <button
          key={t}
          role="tab"
          aria-selected={theme === t}
          aria-label={`${t} theme`}
          className={theme === t ? styles.active : ''}
          onClick={() => apply(t)}
          type="button"
        >
          <span
            className={`${styles.dot} ${styles[`dot_${t}`] ?? ''}`}
            aria-hidden="true"
          />
          {t}
        </button>
      ))}
    </div>
  );
}

export { ThemeSwitch };
